import { db } from '../firebase';
import { 
    collectionGroup, 
    collection,
    query, 
    where, 
    getDocs,
    orderBy,
    startAfter,
    limit,
    doc,
    getDoc,
    updateDoc,
    writeBatch,
    setDoc,
    increment
} from 'firebase/firestore';

/**
 * Fetches the summary statistics for the medication hub
 */
export const fetchMedicationStatsSummary = async (serverId) => {
    try {
        const statsRef = doc(db, 'servers', serverId, 'stats', 'medicineStatsSummary');
        const statsSnap = await getDoc(statsRef);
        
        if (statsSnap.exists()) {
            return statsSnap.data();
        }
        
        // Return default empty state if it doesn't exist
        return {
            totalTimesPrescribed: 0,
            activeConsumerNumber: 0,
            totalMissed: 0,
            totalRefused: 0,
            totalAdministered: 0
        };
    } catch (error) {
        console.error("Error fetching medication stats summary:", error);
        throw error;
    }
};

/**
 * Fetches medications using collectionGroup across all users in a server
 * Supports pagination and filtering by date type (today, upcoming, missed)
 */
export const fetchMedicationsByTab = async (serverId, tabType, lastVisibleDoc = null, pageSize = 20, extraFilters = {}) => {
    try {
        const medsRef = collectionGroup(db, 'myMedications');
        let constraints = [
            where('serverID', '==', serverId)
        ];

        const todayDateString = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const now = new Date();

        switch (tabType) {
            case 'today':
                constraints.push(where('medicateDate', '==', todayDateString));
                break;
            case 'upcoming':
                constraints.push(where('date', '>', now));
                break;
            case 'missed':
            case 'previous':
                constraints.push(where('date', '<', now));
                constraints.push(where('medicateDate', '!=', todayDateString)); 
                break;
            case 'discontinued':
                constraints.push(where('discontinued', '==', true));
                break;
            case 'all':
                // For CD Book, etc.
                break;
            default:
                break;
        }

        if (extraFilters.isControlled) {
            constraints.push(where('isControlled', '==', true));
        }

        // Apply ordering (requires composite indexes in Firestore)
        if (tabType !== 'all') {
            constraints.push(orderBy('date', tabType === 'upcoming' ? 'asc' : 'desc'));
        } else {
            // For CD book, order by date desc
            constraints.push(orderBy('date', 'desc'));
        }
        
        constraints.push(limit(pageSize));

        if (lastVisibleDoc) {
            constraints.push(startAfter(lastVisibleDoc));
        }

        const q = query(medsRef, ...constraints);
        const querySnapshot = await getDocs(q);

        const medications = [];
        querySnapshot.forEach((doc) => {
            medications.push({ id: doc.id, ...doc.data(), ref: doc });
        });

        const lastVisible = querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null;

        return { medications, lastVisible };
    } catch (error) {
        console.error("Error fetching medications by tab:", error);
        throw error;
    }
};

/**
 * Fetches pending controlled drug approvals.
 */
export const fetchPendingCDApprovals = async (serverId) => {
    try {
        const cdRef = collection(db, 'servers', serverId, 'medicationApprovalRequests');
        // Flutter query: where status in pending, queried
        const q = query(
            cdRef, 
            where('status', 'in', ['pending', 'queried']),
            orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const approvals = [];
        querySnapshot.forEach((doc) => {
            approvals.push({ id: doc.id, ...doc.data(), ref: doc });
        });
        return approvals;
    } catch (error) {
        console.error("Error fetching pending CD approvals:", error);
        throw error;
    }
};

/**
 * Updates a pending CD approval (Admin Override, Approve, Reject).
 */
export const reviewCDApproval = async (serverId, requestId, action, comment, adminId, adminName) => {
    try {
        const cdDocRef = doc(db, 'servers', serverId, 'medicationApprovalRequests', requestId);
        let status = 'approved';
        if (action === 'reject') status = 'rejected';
        if (action === 'query') status = 'queried';

        await updateDoc(cdDocRef, {
            status,
            adminOverride: action === 'override' ? true : false,
            adminId,
            adminName,
            comment: comment || '',
            updatedAt: new Date()
        });
        return true;
    } catch (error) {
        console.error("Error reviewing CD approval:", error);
        throw error;
    }
};

/**
 * Fetches Medication Alerts across the server (Missed, Late, Unassigned, Stock Updates)
 */
export const fetchMedicationAlerts = async (serverId) => {
    try {
        // Medication Alerts are stored in servers/{serverId}/medicationAlerts
        const alertsRef = collection(db, 'servers', serverId, 'medicationAlerts');
        const q = query(alertsRef, orderBy('createdAt', 'desc'), limit(100));
        const querySnapshot = await getDocs(q);
        
        const alerts = [];
        querySnapshot.forEach((doc) => {
            alerts.push({ id: doc.id, ...doc.data() });
        });
        return alerts;
    } catch (error) {
        console.error("Error fetching medication alerts:", error);
        throw error;
    }
};

/**
 * Fetches Allocated Medication (Stocks) using collectionGroup
 */
export const fetchAllocatedMedications = async (serverId) => {
    try {
        // Note: We need a composite index on serverID for medicineStocks collectionGroup
        const stocksRef = collectionGroup(db, 'medicineStocks');
        const q = query(stocksRef, where('serverID', '==', serverId), limit(100));
        
        const querySnapshot = await getDocs(q);
        const stocks = [];
        querySnapshot.forEach((doc) => {
            stocks.push({ id: doc.id, ...doc.data() });
        });
        return stocks;
    } catch (error) {
        console.error("Error fetching allocated medications:", error);
        throw error;
    }
};

/**
 * Searches TRUD API for medications
 */
export const searchTRUDMedications = async (query) => {
    try {
        const url = 'https://europe-west2-architectureapp-9e70d.cloudfunctions.net/medicineSearch';
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                limit: 20,
                page: 1,
                search: query
            })
        });
        
        if (!response.ok) throw new Error('TRUD search failed');
        const data = await response.json();
        
        let results = [];
        if (data && data.data && data.data.response) {
            results = data.data.response;
        } else if (data && data.response) {
            results = data.response;
        } else if (data && data.results) {
            results = data.results;
        }
        
        return results.map(item => ({
            id: item.id || item.VPID || item.APID || item.vpid || item.apid || '',
            med_name: item.med_name || item.name || item.medicineName || item.NM || '',
            manufacturer: item.manufacturer || item.supplier || item.supplierName || item.manufacturerName || '',
            dosage: item.dosage || item.strength || '',
            unit: item.unit || item.formulation || item.form || '',
            type: item.trudType || item.type || 'VMP',
            packSize: item.pack_size || item.packSize || item.packLabel || item.quantity || '',
            source: item.source || item.medicineSource || 'TRUD',
            raw: item
        }));
    } catch (error) {
        console.error("Error searching TRUD medications:", error);
        throw error;
    }
};

/**
 * Assigns a medication to a user (replicating flutter _saveForUser logic)
 */
export const assignMedicationToUser = async (serverId, userId, userName, adminId, adminName, medData) => {
    try {
        const batch = writeBatch(db);
        
        const trimMedicineName = medData.id.replace(/\s+/g, '_').toLowerCase();
        
        // 1. Medicine Stats (if we want to track global stats)
        const medStatRef = doc(db, 'servers', serverId, 'medicineStats', trimMedicineName);
        const medStatSnap = await getDoc(medStatRef);
        if (!medStatSnap.exists()) {
            batch.set(medStatRef, {
                id: trimMedicineName,
                name: medData.med_name,
                totalTimesPrescribed: 1
            });
        } else {
            batch.update(medStatRef, {
                totalTimesPrescribed: increment(1)
            });
        }
        
        // 2. Medicine Stock
        const stockRef = doc(db, 'servers', serverId, 'serviceUsers', userId, 'medicineStocks', trimMedicineName);
        const stockSnap = await getDoc(stockRef);
        const requiredQty = medData.requiredQuantity || 30;
        
        if (stockSnap.exists()) {
            batch.update(stockRef, {
                requiredQuantity: increment(requiredQty),
                updatedOn: new Date()
            });
        } else {
            batch.set(stockRef, {
                name: medData.med_name,
                userId: userId,
                userName: userName,
                serverID: serverId,
                currentStock: 0,
                requiredQuantity: requiredQty,
                alertStock: 10,
                updatedOn: new Date()
            });
        }
        
        // 3. Create Daily MyMedications entries (simplification of flutter date loop)
        // Flutter loops over selectedDates and creates a doc for each day.
        // We will just create one for today to represent the assignment starting today.
        // In a full implementation, you loop over start/end dates.
        const day = new Date();
        const dateString = day.toISOString().split('T')[0];
        
        // Generate a random ID for the daily log
        const myMedsId = doc(collection(db, 'servers')).id; 
        const myMedsRef = doc(db, 'servers', serverId, 'serviceUsers', userId, 'myMedications', myMedsId);
        
        batch.set(myMedsRef, {
            date: day,
            medicateDate: dateString,
            medicineName: medData.med_name,
            timing: medData.timings || ["08:00"],
            medicineID: trimMedicineName,
            medicationKey: trimMedicineName,
            catalogueMedicineID: medData.id,
            displayMedicineName: medData.med_name,
            medicineSource: medData.source,
            isFromTrudRegistry: medData.source === 'TRUD',
            trudId: medData.id,
            isControlled: medData.isControlled || false,
            serverID: serverId,
            userID: userId,
            assignedBy: adminName,
            assignedById: adminId,
            createdAt: new Date(),
            status: "pending", // Waiting to be administered
            route: medData.route || "Oral",
            dosage: medData.dosage || "1",
            unit: medData.unit || "tablet",
        });

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error assigning medication:", error);
        throw error;
    }
};
